import React, { Component } from "react";
import { Select, Slider, Row, Col } from "antd";
import {
  INDICATORS,
  formatPercent,
  LEVEL_LAYERS,
  WATERSHEDS,
  MUNI_NAMES,
} from "../../configs/constants";
import { InputGroup } from "../../components";
import _ from "lodash";
import "./Filters.css";
import { LEVELS } from "../../types";

const { Option } = Select;

const SLIDER_DEFAULTS = {
  range: true,
  tipFormatter: formatPercent,
  step: 0.01,
};

type FilterProps = {
  map: mapboxgl.Map;
  level: "MUNICIPALITY" | "PARCEL" | "BLOCKGROUP" | "CANOPY";
};

type AcreRange = {
  label: string;
  range: [number, number] | undefined;
};

const ACRE_RANGES: AcreRange[] = [
  {
    label: "All",
    range: undefined,
  },
  {
    label: "≦1",
    range: [0, 1],
  },
  {
    label: "1-5",
    range: [1, 5],
  },
  {
    label: ">5",
    range: [5, 1e9],
  },
];

class Filters extends Component<FilterProps> {
  filtersWithoutProp(layer: string, prop: string) {
    return (this.props.map.getFilter(layer) || ["all"]).filter((element) => {
      if (!Array.isArray(element)) return true;
      let target = element[1];
      if (Array.isArray(target)) target = target[1];
      return target !== prop;
    });
  }
  setRange(level: LEVELS, prop: string, [low, high]: [number, number]) {
    const filters = this.filtersWithoutProp(
      LEVEL_LAYERS[level][0],
      prop
    ).concat([
      [">=", prop, low],
      ["<=", prop, high],
    ]);
    this.setLevelFilters(level, filters);
  }
  getHez() {
    const filter = this.getFilter("PARCEL");
    if (filter.find((filter) => Array.isArray(filter) && filter[1] === "HEZ"))
      return "true";
    if (
      filter.find(
        (filter) =>
          Array.isArray(filter) && filter[0] === "!" && filter[1][1] === "HEZ"
      )
    ) {
      return "false";
    }
  }
  setHez(value?: boolean) {
    const filters = this.filtersWithoutProp(LEVEL_LAYERS["PARCEL"][0], "HEZ");
    if (typeof value === "boolean") {
      filters.push([value ? "!=" : "==", "HEZ", " "]);
    }
    this.setLevelFilters("PARCEL", filters);
    this.setLevelFilters("BLOCKGROUP", filters);
    this.forceUpdate();
  }

  getFilter(level: LEVELS) {
    const layer = LEVEL_LAYERS[level][0];
    if (!this.props.map) return [];
    if (!this.props.map.getLayer(layer)) return [];
    return this.props.map.getFilter(layer) || [];
  }
  getRange = (level: LEVELS, prop: string): [number, number] | undefined => {
    const bounds = this.getFilter(level).filter(
      (element) => Array.isArray(element) && element[1] === prop
    );
    let upper = bounds.find((bound) => bound[0] === "<=");
    let lower = bounds.find((bound) => bound[0] === ">=");
    if (upper && lower) {
      return [lower[2], upper[2]];
    }
  };
  setEq(level: LEVELS, prop: string, val: any) {
    const filters = this.filtersWithoutProp(LEVEL_LAYERS[level][0], prop);
    if (prop === "Public" && val !== "Y") {
      // you need both of these to capture privates!
      filters.push(["!=", prop, "Y"]);
    } else {
      if (val) filters.push(["==", prop, val]);
    }
    this.setLevelFilters(level, filters);
  }
  getEq(level: LEVELS, prop: string) {
    const filter = this.getFilter(level).find(
      (element) => Array.isArray(element) && element[1] === prop
    );
    if (filter && prop === "Public" && filter[0] === "!=") {
      // basically, you need to manually return privates because of the whole not equal situation
      return "Private";
    }
    if (filter) return filter[2];
  }
  setLevelFilters = (level: LEVELS, filters: any) => {
    for (let layer of LEVEL_LAYERS[level])
      this.props.map.setFilter(layer, filters);
  };

  reset = () => {
    for (let level in LEVEL_LAYERS) {
      this.setLevelFilters(level as LEVELS, null);
    }
    this.forceUpdate();
  };

  render() {
    const { level } = this.props;

    return (
      <div className="filters">
        <div className="filters__content">
          {level === "MUNICIPALITY" && (
            <p className="note">Zoom in to use filters.</p>
          )}
          {(level === "PARCEL" || level === "CANOPY") && (
            <>
              <div className="filters__heading">Parcel filters</div>
              <Row gutter={20}>
                <Col key={"parcel-area"} sm={12}>
                  <InputGroup label="Acres">
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr 1fr",
                        gridColumnGap: "5px",
                      }}
                    >
                      {ACRE_RANGES.map((acreRange) => (
                        <button
                          key={acreRange.label}
                          className={
                            _.isEqual(
                              this.getRange("PARCEL", "TotalAcres"),
                              acreRange.range
                            )
                              ? "ant-btn-primary"
                              : "ant-btn"
                          }
                          style={{
                            padding: 2,
                            fontSize: 12,
                          }}
                          onClick={() => {
                            if (acreRange.range === undefined) {
                              this.setLevelFilters(
                                "PARCEL",
                                this.filtersWithoutProp(
                                  LEVEL_LAYERS.PARCEL[0],
                                  "TotalAcres"
                                )
                              );
                            } else {
                              this.setRange(
                                "PARCEL",
                                "TotalAcres",
                                acreRange.range
                              );
                            }
                            this.forceUpdate();
                          }}
                        >
                          {acreRange.label}
                        </button>
                      ))}
                    </div>
                  </InputGroup>
                </Col>
                <Col key={"parcel-treecanopy-percent"} sm={12}>
                  {[INDICATORS.TREE_CANOPY_PERCENT].map(
                    ({
                      field,
                      parcelField,
                      short,
                      range,
                      explanation,
                      sliderOptions = {},
                    }) => {
                      return (
                        <InputGroup label={short} tooltip={explanation} key={1}>
                          <Slider
                            value={this.getRange("BLOCKGROUP", field) || range}
                            min={range![0]}
                            max={range![1]}
                            {...SLIDER_DEFAULTS}
                            {...sliderOptions}
                            onChange={(value: [number, number]) => {
                              this.setRange("BLOCKGROUP", field, value);
                              this.setRange(
                                "PARCEL",
                                parcelField || field,
                                value
                              );
                              this.forceUpdate();
                            }}
                          />
                        </InputGroup>
                      );
                    }
                  )}
                </Col>
                <Col key={"parcel-possible-treecanopy-percent"} sm={12}>
                  <InputGroup label="Possible Canopy %" tooltip="">
                    <Slider
                      value={this.getRange("PARCEL", "PossTCPct") || [0, 5.0]}
                      min={0}
                      max={1.1}
                      range={true}
                      tipFormatter={formatPercent}
                      step={0.01}
                      onChange={(value) => {
                        this.setRange("PARCEL", "PossTCPct", value);
                        this.forceUpdate();
                      }}
                    />
                  </InputGroup>
                </Col>
                <Col sm={12}>
                  <InputGroup
                    label="Health Equity Zone"
                    tooltip="Geographic areas where existing opportunities emerge and investments are made to address differences in health outcomes."
                  >
                    <Select
                      allowClear
                      style={{ width: "100%" }}
                      value={this.getHez()}
                      onChange={(value) => {
                        this.setHez(
                          value === undefined ? undefined : value === "true"
                        );
                      }}
                    >
                      <Option value="true">Yes</Option>
                      <Option value="false">No</Option>
                    </Select>
                  </InputGroup>
                </Col>
                <Col sm={24}>
                  <InputGroup label="Watershed">
                    <Select
                      allowClear
                      style={{ width: "100%" }}
                      value={this.getEq("PARCEL", "Watershed")}
                      onChange={(value) => {
                        this.setEq("PARCEL", "Watershed", value);
                        this.forceUpdate();
                      }}
                    >
                      {WATERSHEDS.map((watershed, i) => (
                        <Option key={i} value={watershed}>
                          {watershed}
                        </Option>
                      ))}
                    </Select>
                  </InputGroup>
                </Col>
                <Col sm={12}>
                  <InputGroup label="School">
                    <Select
                      allowClear
                      style={{ width: "100%" }}
                      value={this.getEq("PARCEL", "School")}
                      onChange={(value) => {
                        this.setEq("PARCEL", "School", value);
                        this.forceUpdate();
                      }}
                    >
                      <Option value={"Y"}>Yes</Option>
                      <Option value={"N"}>No</Option>
                    </Select>
                  </InputGroup>
                </Col>
                <Col sm={12}>
                  <InputGroup label="Ownership">
                    <Select
                      allowClear
                      style={{ width: "100%" }}
                      value={this.getEq("PARCEL", "Public")}
                      onChange={(value) => {
                        this.setEq("PARCEL", "Public", value);
                        this.forceUpdate();
                      }}
                    >
                      <Option value={"Y"}>Public</Option>
                      <Option value={""}>Private</Option>
                    </Select>
                  </InputGroup>
                </Col>
                <Col sm={12}>
                  <InputGroup label="Flood risk">
                    <Select
                      allowClear
                      style={{ width: "100%" }}
                      value={this.getEq("PARCEL", "Flood")}
                      onChange={(value) => {
                        this.setEq("PARCEL", "Flood", value);
                        this.forceUpdate();
                      }}
                    >
                      <Option value={"High"}>High</Option>
                      <Option value={"Moderate"}>Moderate</Option>
                      <Option value={"Low"}>Low</Option>
                    </Select>
                  </InputGroup>
                </Col>
              </Row>
            </>
          )}
          {level === "BLOCKGROUP" && (
            <Row gutter={20}>
              {[
                INDICATORS.TREE_EQUITY_SCORE,
                INDICATORS.TREE_CANOPY_PERCENT,
                INDICATORS.TREE_CANOPY_GAP,
                INDICATORS.PEOPLE_OF_COLOR,
                INDICATORS.POVERTY_PERCENT,
                INDICATORS.UNEMPLOYMENT_RATE,
                INDICATORS.CHILDREN_PERCENT,
                INDICATORS.SENIOR_PERCENT,
              ].map(
                ({
                  field,
                  parcelField,
                  short,
                  range,
                  explanation,
                  sliderOptions = {},
                }) => {
                  return (
                    <Col key={field} sm={12}>
                      <InputGroup label={short} tooltip={explanation}>
                        <Slider
                          value={this.getRange("BLOCKGROUP", field) || range}
                          min={range![0]}
                          max={range![1]}
                          {...SLIDER_DEFAULTS}
                          {...sliderOptions}
                          onChange={(value: [number, number]) => {
                            this.setRange("BLOCKGROUP", field, value);
                            this.setRange(
                              "PARCEL",
                              parcelField || field,
                              value
                            );
                            this.forceUpdate();
                          }}
                        />
                      </InputGroup>
                    </Col>
                  );
                }
              )}
              <Col sm={12}>
                <InputGroup
                  label="Climate Health risk"
                  tooltip="High, moderate and low numbers of hospitalizations and emergency room visits for conditions that are exacerbated by a changing climate. The conditions combined in this measure include diabetes, mental health, asthma and other respiratory issues, heart and kidney diseases."
                >
                  <Select
                    allowClear
                    style={{ width: "100%" }}
                    value={this.getEq("BLOCKGROUP", "HealthRisk")}
                    onChange={(value) => {
                      this.setEq("BLOCKGROUP", "HealthRisk", value);
                      this.setEq("PARCEL", "HealthRisk", value);
                      this.forceUpdate();
                    }}
                  >
                    <Option value={"High"}>High</Option>
                    <Option value={"Moderate"}>Moderate</Option>
                    <Option value={"Low"}>Low</Option>
                  </Select>
                </InputGroup>
              </Col>
              <Col sm={12}>
                <InputGroup label="Municipality">
                  <Select
                    allowClear
                    style={{ width: "100%" }}
                    value={this.getEq("BLOCKGROUP", "incorpname")}
                    onChange={(value) => {
                      this.setEq("BLOCKGROUP", "incorpname", value);
                      this.setEq("PARCEL", "Municipali", value);
                      this.forceUpdate();
                    }}
                  >
                    {MUNI_NAMES.map((name, i) => (
                      <Option key={i} value={name}>
                        {name}
                      </Option>
                    ))}
                  </Select>
                </InputGroup>
              </Col>
              <Col sm={12}>
                <InputGroup
                  label="Health Equity Zone"
                  tooltip="Geographic areas where existing opportunities emerge and investments are made to address differences in health outcomes."
                >
                  <Select
                    allowClear
                    style={{ width: "100%" }}
                    value={this.getHez()}
                    onChange={(value) => {
                      this.setHez(
                        value === undefined ? undefined : value === "true"
                      );
                    }}
                  >
                    <Option value="true">Yes</Option>
                    <Option value="false">No</Option>
                  </Select>
                </InputGroup>
              </Col>
            </Row>
          )}
        </div>

        {level !== "MUNICIPALITY" && (
          <div className="filters__button-row">
            <button className="ant-btn" onClick={this.reset}>
              Reset
            </button>
          </div>
        )}
      </div>
    );
  }
}

export default Filters;
